import Lexical, {$createParagraphNode, $createTextNode, $getRoot, $isTextNode} from "lexical";
import {convertRawDraftContentStateToLexicalEditorState} from "../../utils/DraftToLexical";
import {LexicalUtil} from "../../utils/LexicalUtil";
import {TextStyle} from "./TextStyle";
import {
    AlignStyleType,
    CHANGE_TEXT_STYLE_TYPE,
    ColorStyleType, FontStyleType,
    SizeStyleType,
    TOGGLE_TEXT_STYLE_TYPE
} from "./TextStyleType";
import {TextStructure} from "./TextStructure";

export type RawJcEditorState = {
    blocks?: any; /* DraftJS (旧式: 本番データのコンパクションができれば対応をなくせる)  */
    _nodeMap?: any; /* Lexical */
    text_structure: object;
};

export class JcEditorState {

    private readonly editor_state: Lexical.EditorState;
    private text_style: TextStyle | null = null;

    constructor(editor_state: Lexical.EditorState) {
        this.editor_state = editor_state;
    }

    toJSON(): RawJcEditorState {
        // Lexical の データ構造が変化した際に大変なので、 別途専用のデータ構造で保存することも考えたが、
        // 結局は Lexical の内部構造を利用することになると思うので、ひとまず そのまま。
        // と思ったら、 Lexical が 0.3.0 でいきなり serialize フォーマットを変えてきたので、TextStructure で保存するようにする
        return {
            text_structure: this.toTextStructure().toJSON(),
        };
    }

    toTextStructure() {
        return TextStructure.fromLexicalEditorState(this.editor_state);
    }

    isEmpty(): boolean {
        return '' === this.editor_state.read(() => {
            return Lexical.$getRoot().getTextContent();
        });
    }

    toText(): string {
        return this.editor_state.read(() => {
            return $getRoot().getChildren().map(node =>
                (Lexical.$isParagraphNode(node) ? node.getChildren().map(n => $isTextNode(n) ? n.getTextContent() : '') : []).join('')
            ).join('\n');
        });
    }

    getInternalEditorState(): Lexical.EditorState {
        return this.editor_state;
    }

    withLexicalEditorState(editor_state: Lexical.EditorState) {
        return new JcEditorState(editor_state);
    }

    static createFromRaw(raw: RawJcEditorState | any): JcEditorState {

        let json: Lexical.SerializedEditorState | null = null;
        if (raw.blocks !== undefined) {
            json = convertRawDraftContentStateToLexicalEditorState(raw);
        } else if (raw._nodeMap !== undefined) {
            json = TextStructure.fromOldRawLexicalEditorState(raw).toRawLexicalEditorState();
        } else if (raw.text_structure) {
            json = TextStructure.fromJSON(raw.text_structure).toRawLexicalEditorState();
        }

        if (json === null) {
            throw new Error('unexpected RawJcEditorState');
        }

        const lexical_editor_state = Lexical.createEditor().parseEditorState(json);
        return new JcEditorState(lexical_editor_state);
    }

    static createFromTextStructure(text_stricture: TextStructure): JcEditorState {
        return new JcEditorState(Lexical.createEditor().parseEditorState(text_stricture.toRawLexicalEditorState()));
    }

    static createEmpty(): JcEditorState {
        // 元々は、 `return new JcEditorState(Lexical.createEditor().getEditorState());` のみ
        // 不必要に node を作る必要は無いので、 上記でも十分なのだが、
        // 下記の通りに Node を生成しておくことで、RCContentCell::handleChangeContent -> CJMapManager.setData が呼ばれ
        // CellContent の editor_type が確定する.
        // これにより、 新規セルに対しても Paste が機能するようになる
        //
        // 非効率な挙動なので、後々修正したいところ

        const lexical_editor_state = LexicalUtil.interceptPendingEditorState(() => {
            const root = $getRoot();
            root.append($createParagraphNode().append($createTextNode('')));
        });
        return new JcEditorState(lexical_editor_state);
    }

    static createEmptyCentering(): JcEditorState {
        const lexical_editor_state = LexicalUtil.interceptPendingEditorState(() => {
            const root = $getRoot();
            root.setFormat('center');
            root.append($createParagraphNode().append($createTextNode('')));
        });
        return new JcEditorState(lexical_editor_state);
    }

    getTextStyle(): TextStyle {
        if (this.text_style === null) {
            this.text_style = TextStyle.fromLexicalEditorState(this.editor_state)
        }
        return this.text_style;
    }

    changeTextStyle(style_type: CHANGE_TEXT_STYLE_TYPE, value: string): JcEditorState {
        if (style_type === 'align') {
            return JcEditorState.createFromTextStructure(
                TextStructure.fromLexicalEditorState(this.editor_state).setAlign(AlignStyleType.fromString(value))
            );
        } else if (style_type === 'color') {
            const color = ColorStyleType.fromCssValueOrDefault(value, null);
            if (color === null) {
                throw new Error('invalid color');
            }
            return JcEditorState.createFromTextStructure(
                TextStructure.fromLexicalEditorState(this.editor_state).setColor(color)
            );
        } else if (style_type === 'size') {
            return JcEditorState.createFromTextStructure(
                TextStructure.fromLexicalEditorState(this.editor_state).setSize(SizeStyleType.fromString("size_style-" + value))
            );
        } else if (style_type === 'font') {
            return JcEditorState.createFromTextStructure(
                TextStructure.fromLexicalEditorState(this.editor_state).setFont(FontStyleType.fromString("font_style-" + value))
            );
        }

        throw new Error("invalid style_type");
    }

    toggleTextStyle(style_type: TOGGLE_TEXT_STYLE_TYPE): JcEditorState {
        if (style_type === 'bold') {
            return JcEditorState.createFromTextStructure(
                TextStructure.fromLexicalEditorState(this.editor_state).toggleBold()
            );
        }
        throw new Error("invalid style_type");
    }

}
